import type { DocumentFilter } from '@/types/measure-document-types';
import type { FilterState } from '@/types/nmed/shared-filter-types';
import { isRangeFilter } from '@/types/nmed/shared-filter-types';
import { splitSchemaFieldKey } from '@/utils/schema-field-key';

/** Bureau and DocType keep dedicated UI, but they are ordinary schema fields underneath. */
const BUREAU = { group: 'CLASSIFICATION', code: 'BUREAU' } as const;
const DOCTYPE = { group: 'CLASSIFICATION', code: 'DOCTYPE' } as const;

export interface FilterStateToDocumentFiltersOptions {
   /**
    * Called with each filter key that could not be resolved to a group and code.
    * Keys are dropped rather than guessed — surface them so the user isn't shown
    * results narrower or wider than the filters they can see.
    */
   onUnmappable?: (key: string) => void;
}

function yearRange(from: string, to: string): { start: string; end: string } | null {
   if (!/^\d{4}$/.test(from) || !/^\d{4}$/.test(to))
      return null;
   // An inverted range would emit `start` after `end`, which the backend matches
   // against nothing — an empty result set the user has no way to explain. Drop
   // it like a half-filled range instead.
   if (Number(from) > Number(to))
      return null;
   return { start: `${from}-01-01`, end: `${to}-12-31` };
}

/**
 * Translate the UI's filter state into the API's `DocumentFilter[]`.
 *
 * One filter per selected value: the backend ORs repeated (group, code) pairs
 * and ANDs across distinct fields, which is exactly multi-select semantics.
 *
 * Nothing here guesses. The backend skips a filter whose group/code doesn't
 * resolve, so a mistranslation would silently *widen* the result set rather than
 * error — an unresolvable key is dropped and reported instead.
 */
export function filterStateToDocumentFilters(
   filterState: FilterState,
   options: FilterStateToDocumentFiltersOptions = {},
): DocumentFilter[] {
   const filters: DocumentFilter[] = [];

   for (const bureau of filterState.bureaus) {
      if (bureau.trim())
         filters.push({ ...BUREAU, value: bureau });
   }

   for (const docType of filterState.documentTypes) {
      if (docType.trim())
         filters.push({ ...DOCTYPE, value: docType });
   }

   for (const [key, values] of Object.entries(filterState.searchKeyword.filters)) {
      const field = splitSchemaFieldKey(key);
      if (!field) {
         options.onUnmappable?.(key);
         continue;
      }
      for (const value of values) {
         if (isRangeFilter(value)) {
            filters.push({
               ...field,
               value: { start: String(value.start_at), end: String(value.end_at) },
            });
         }
         else if (value.trim()) {
            filters.push({ ...field, value });
         }
      }
   }

   // Year has no code: it spans every DATE field, matching a document whose date
   // in *any* DATE code falls in the range — the legacy year filter's behaviour.
   const { type, year, startYear, endYear } = filterState.year;
   const range = type === 'single' ? yearRange(year, year) : yearRange(startYear, endYear);
   if (range)
      filters.push({ group: 'DATE', value: range });

   return filters;
}
